import React from 'react';
import { processSteps } from '../mockData';
import { Phone, Lightbulb, Layers, Hammer, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const iconMap = {
  Phone,
  Lightbulb,
  Layers,
  Hammer,
  CheckCircle
};

const Process = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-6 font-poppins">
              Our Process
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              A transparent, collaborative journey from concept to completion. Here's how we transform your vision into reality.
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-12">
              {processSteps.map((step, index) => {
                const Icon = iconMap[step.icon];
                return (
                  <div key={step.id} className="relative">
                    {/* Connector Line */}
                    {index < processSteps.length - 1 && (
                      <div className="absolute left-8 top-20 w-0.5 h-full bg-[#E8DCC4] hidden md:block"></div>
                    )}
                    
                    <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                      <CardContent className="p-8">
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Icon and Step Number */}
                          <div className="flex-shrink-0">
                            <div className="relative">
                              <div className="w-16 h-16 bg-[#2F4538] rounded-full flex items-center justify-center">
                                <Icon className="w-8 h-8 text-white" />
                              </div>
                              <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#E8DCC4] rounded-full flex items-center justify-center border-2 border-white">
                                <span className="text-sm font-bold text-[#2C2C2C]">{step.id}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold text-[#2C2C2C] mb-3 font-poppins">
                              {step.title}
                            </h3>
                            <p className="text-gray-700 leading-relaxed text-lg">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Points */}
      <section className="py-20 bg-[#E8DCC4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-12 font-poppins text-center">
              Our Commitment to You
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Professionalism',
                  description: 'Every interaction reflects our commitment to excellence and respect for your time and investment.'
                },
                {
                  title: 'Responsiveness',
                  description: 'Quick replies, regular updates, and always available to address your questions and concerns.'
                },
                {
                  title: 'Transparency',
                  description: 'Clear timelines, detailed quotations, and honest communication at every stage of the project.'
                }
              ].map((point, index) => (
                <Card key={index} className="border-none shadow-md">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-semibold text-[#2C2C2C] mb-3 font-poppins">{point.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{point.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <img
              src="https://images.pexels.com/photos/5922204/pexels-photo-5922204.jpeg"
              alt="Planning"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
            />
            <img
              src="https://images.pexels.com/photos/834892/pexels-photo-834892.jpeg"
              alt="Blueprints"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
            />
            <img
              src="https://images.pexels.com/photos/4101143/pexels-photo-4101143.jpeg"
              alt="Consultation"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2F4538] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-poppins">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's begin your design journey with a conversation about your vision and needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-white text-[#2F4538] hover:bg-gray-100 transition-all hover:scale-105">
              <Link to="/contact">Schedule Your Consultation</Link>
            </Button>
            <span className="text-lg">or call</span>
            <a href="tel:+919910837533" className="text-2xl font-bold hover:underline">
              +91 99108 37533
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Process;